import React, { useState, useEffect } from 'react';
import { Upload, RefreshCw } from 'lucide-react';
import AvailabilitySlotForm from './AvailabilitySlotForm';
import AvailabilityCalendar from './AvailabilityCalendar';
import ConflictWarningModal from './ConflictWarningModal';
import BookingFeeCard from './BookingFeeCard';
import DeleteWarningDialog from './DeleteWarningDialog';
import MaxAppointmentsWarningDialog from './MaxAppointmentsWarningDialog';
import { availabilityAPI, appointmentAPI, partnerAPI, googleCalendarAPI } from '../../services/api';
import { formatTime, getUserTimezone } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';

const AvailabilityTab = ({ partnerId }) => {
  const { user } = useAuth();
  const [slots, setSlots] = useState([]);

  const [formData, setFormData] = useState({
    date: '',
    start_time: '',
    therapy_type: '',
    session_mode: 'available_online',
    dateMode: 'single',
    date_start: '',
    date_end: '',
    selected_days: [0, 1, 2, 3, 4, 5, 6]
  });
  const [conflictWarning, setConflictWarning] = useState(null);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [pendingSlotData, setPendingSlotData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteDialogSlot, setDeleteDialogSlot] = useState(null);
  const [deleteDialogAction, setDeleteDialogAction] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loadingPaymentInfo, setLoadingPaymentInfo] = useState(false);
  const [showMaxAppointmentsWarning, setShowMaxAppointmentsWarning] = useState(false);
  const [maxAppointmentsWarningValue, setMaxAppointmentsWarningValue] = useState(null);
  const [dontShowMaxAppointmentsWarningAgain, setDontShowMaxAppointmentsWarningAgain] = useState(false);
  const [feeSettings, setFeeSettings] = useState(null);
  const [feeSettingsLoaded, setFeeSettingsLoaded] = useState(false);
  const [googleCalendarConnected, setGoogleCalendarConnected] = useState(false);

  useEffect(() => {
    loadSlots();
  }, [partnerId]);

  useEffect(() => {
    loadFeeSettings();
    loadGoogleCalendarStatus();
  }, [partnerId]);

  const loadFeeSettings = async () => {
    try {
      const response = await partnerAPI.getFeeSettings(partnerId);
      setFeeSettings(response.data.feeSettings);
      setFeeSettingsLoaded(true);
    } catch (error) {
      console.error('Failed to load fee settings:', error);
      setFeeSettingsLoaded(true);
    }
  };

  const loadGoogleCalendarStatus = async () => {
    try {
      const response = await googleCalendarAPI.getStatus();
      setGoogleCalendarConnected(response.data.connected || false);
    } catch (error) {
      console.error('Failed to load Google Calendar status:', error);
      setGoogleCalendarConnected(false);
    }
  };

  const getMaxAppointmentsLimit = () => {
    const raw = user?.subscription?.max_appointments;
    if (raw === null || raw === undefined) return null;
    const parsed = typeof raw === 'string' ? parseInt(raw, 10) : raw;
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getMaxAppointmentsWarningDismissKey = (maxAppointments) => {
    if (maxAppointments === null || maxAppointments === undefined) return null;
    // Scope by partner + plan limit so upgrades (9 -> 25) can show again
    return `availability:maxAppointmentsWarning:dismissed:${partnerId}:${maxAppointments}`;
  };

  const isMaxAppointmentsWarningDismissed = (maxAppointments) => {
    const key = getMaxAppointmentsWarningDismissKey(maxAppointments);
    if (!key) return false;
    return localStorage.getItem(key) === 'true';
  };

  const getMonthKey = (slotDate) => {
    if (!slotDate) return null;
    const dateStr = typeof slotDate === 'string' ? slotDate.split('T')[0] : String(slotDate);
    return dateStr.length >= 7 ? dateStr.slice(0, 7) : null; // YYYY-MM
  };

  const countAvailableSlotsInMonth = (monthKey) => {
    if (!monthKey) return 0;
    return slots.filter((s) => {
      const status = s?.status || '';
      if (!status.startsWith('available')) return false;
      return getMonthKey(s?.slot_date) === monthKey;
    }).length;
  };

  const checkAndShowMaxAppointmentsWarning = async () => {
    const maxAppointments = getMaxAppointmentsLimit();
    if (maxAppointments === null) return false;
    if (isMaxAppointmentsWarningDismissed(maxAppointments)) return false;

    try {
      // Check current month appointment count
      const response = await appointmentAPI.getCurrentMonthCount(partnerId);
      const currentAppointmentCount = response.data.count || 0;

      // Show warning if current appointment count has reached or exceeded the limit
      // This means creating a new availability slot could lead to exceeding the limit
      if (currentAppointmentCount >= maxAppointments) {
        setMaxAppointmentsWarningValue(maxAppointments);
        setDontShowMaxAppointmentsWarningAgain(false);
        setShowMaxAppointmentsWarning(true);
        return true;
      }
    } catch (error) {
      console.error('Failed to check appointment count:', error);
      // Don't block slot creation if we can't check the count
    }

    return false;
  };

  /**
   * Load availability slots for next 4 weeks (28 days)
   * Use local timezone for date formatting
   */
  const loadSlots = async () => {
    try {
      setLoading(true);
      const today = new Date();

      // Format start date in local timezone (YYYY-MM-DD)
      const startYear = today.getFullYear();
      const startMonth = String(today.getMonth() + 1).padStart(2, '0');
      const startDay = String(today.getDate()).padStart(2, '0');
      const startDate = `${startYear}-${startMonth}-${startDay}`;

      // Calculate end date (27 days from today = 28 days total including today)
      const endDateObj = new Date(today);
      endDateObj.setDate(endDateObj.getDate() + 27);
      const endYear = endDateObj.getFullYear();
      const endMonth = String(endDateObj.getMonth() + 1).padStart(2, '0');
      const endDay = String(endDateObj.getDate()).padStart(2, '0');
      const endDateStr = `${endYear}-${endMonth}-${endDay}`;

      const response = await availabilityAPI.getPartnerSlots(partnerId, startDate, endDateStr);
      setSlots(response.data.slots || []);
    } catch (error) {
      console.error('Failed to load slots:', error);
      alert('Failed to load availability slots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle slot creation
   */
  const handleCreateSlot = async () => {
    // Check if at least one therapy fee is set
    const hasTherapyFees = feeSettings?.therapy_fees && feeSettings.therapy_fees.length > 0;
    const hasLegacyFee = feeSettings?.session_fee !== null && feeSettings?.session_fee !== '';

    if (!hasTherapyFees && !hasLegacyFee) {
      alert('Please set your session fee for at least one therapy type in the "Set Session Fee" section before creating availability slots.');
      return;
    }

    try {
      setLoading(true);

      // Check appointment count BEFORE creating the slot
      const shouldShowWarning = await checkAndShowMaxAppointmentsWarning();
      if (shouldShowWarning) {
        // Store the slot data to create after warning is acknowledged
        setPendingSlotData({
          partner_id: partnerId,
          slot_date: formData.date,
          start_time: formData.start_time,
          therapy_type: formData.therapy_type,
          status: formData.session_mode,
          timezone: getUserTimezone()
        });
        setLoading(false);
        return;
      }

      if (formData.dateMode === 'range') {
        // Bulk creation mode
        const bulkData = {
          partner_id: partnerId,
          date_start: formData.date_start,
          date_end: formData.date_end,
          selected_days: formData.selected_days,
          start_time: formData.start_time,
          therapy_type: formData.therapy_type,
          status: formData.session_mode,
          timezone: getUserTimezone()
        };

        const response = await availabilityAPI.createBulkSlots(bulkData);
        const summary = response.data.summary;
        let message = `${summary.created} slot(s) created.`;
        if (summary.conflicts > 0) message += ` ${summary.conflicts} had calendar conflicts.`;
        if (summary.duplicates > 0) message += ` ${summary.duplicates} duplicate(s) skipped.`;
        alert(message);
        resetForm();
        loadSlots();
        return;
      }

      const slotData = {
        partner_id: partnerId,
        slot_date: formData.date,
        start_time: formData.start_time,
        therapy_type: formData.therapy_type,
        status: formData.session_mode,
        timezone: getUserTimezone()
      };

      const response = await availabilityAPI.createSlot(slotData);

      // Check if there's a conflict warning
      if (response.data.conflict_warning && response.data.conflict_warning.has_conflict) {
        setPendingSlotData(slotData);
        setConflictWarning(response.data.conflict_warning);
        setShowConflictModal(true);
        setLoading(false);
        return;
      }

      // Success - no conflict or slot created anyway
      alert('Availability slot created successfully!');
      resetForm();
      loadSlots();
    } catch (error) {
      console.error('Failed to create slot:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create availability slot';
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Proceed with creation despite conflict
   */
  const proceedWithCreation = () => {
    setShowConflictModal(false);
    setConflictWarning(null);
    setPendingSlotData(null);
    // Slot was already created, just reload
    alert('Availability slot created successfully (despite conflict)!');
    resetForm();
    loadSlots();
  };

  /**
   * Proceed with slot creation after max appointments warning is acknowledged
   */
  const proceedWithSlotCreationAfterWarning = async () => {
    if (!pendingSlotData) return;

    try {
      setLoading(true);
      const response = await availabilityAPI.createSlot(pendingSlotData);

      // Check if there's a conflict warning
      if (response.data.conflict_warning && response.data.conflict_warning.has_conflict) {
        setConflictWarning(response.data.conflict_warning);
        setShowConflictModal(true);
        setLoading(false);
        return;
      }

      // Success - no conflict or slot created anyway
      alert('Availability slot created successfully!');
      resetForm();
      loadSlots();
    } catch (error) {
      console.error('Failed to create slot:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create availability slot';
      alert(errorMessage);
    } finally {
      setLoading(false);
      setPendingSlotData(null);
    }
  };

  /**
   * Cancel conflict modal
   */
  const cancelConflict = () => {
    setShowConflictModal(false);
    setConflictWarning(null);
    setPendingSlotData(null);
    setLoading(false);
  };

  /**
   * Reset form to default values (keeping therapy_type selection)
   */
  const resetForm = () => {
    setFormData({
      date: '',
      start_time: '',
      therapy_type: formData.therapy_type, // Preserve the therapy type selection
      session_mode: formData.session_mode, // Preserve the session mode selection
      dateMode: formData.dateMode,
      date_start: '',
      date_end: '',
      selected_days: [0, 1, 2, 3, 4, 5, 6]
    });
  };

  /**
   * Handle edit slot
   */
  const handleEdit = async (slot) => {
    // Populate form with slot data
    const availableStatuses = ['available_online', 'available_offline', 'available_offline_online'];
    const slotSessionMode = availableStatuses.includes(slot.status) ? slot.status : 'available_offline_online';
    setFormData({
      date: slot.slot_date,
      start_time: slot.start_time,
      therapy_type: slot.therapy_type || '',
      session_mode: slotSessionMode,
      dateMode: 'single',
      date_start: '',
      date_end: '',
      selected_days: [0, 1, 2, 3, 4, 5, 6]
    });

    // Delete the old slot
    try {
      await availabilityAPI.deleteSlot(slot.id);
      loadSlots();
    } catch (error) {
      console.error('Failed to delete slot for editing:', error);
      alert('Failed to edit slot. Please try again.');
    }
  };

  /**
   * Load payment information for a slot
   */
  const loadPaymentInfo = async (slotId) => {
    try {
      setLoadingPaymentInfo(true);
      const response = await availabilityAPI.getPaymentInfo(slotId);
      setPaymentInfo(response.data);
    } catch (error) {
      console.error('Failed to load payment info:', error);
      setPaymentInfo(null);
    } finally {
      setLoadingPaymentInfo(false);
    }
  };

  /**
   * Handle delete slot (for unbooked slots only)
   */
  const handleDelete = async (slot) => {
    const bookedStatuses = ['booked', 'confirmed', 'confirmed_balance_pending', 'confirmed_payment_pending'];
    const isBooked = bookedStatuses.includes(slot.status);
    
    if (isBooked) {
      // For booked slots, show warning dialog
      setDeleteDialogSlot(slot);
      setDeleteDialogAction('deleteSlotAndBooking');
      await loadPaymentInfo(slot.id);
      setShowDeleteDialog(true);
      return;
    }

    // For unbooked slots, simple confirmation
    const timeRange = `${formatTime(slot.start_datetime)} - ${formatTime(slot.end_datetime)}`;
    const confirmMessage = `Are you sure you want to delete this availability slot?\n\n` +
      `Date: ${slot.slot_date}\nTime: ${timeRange}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await availabilityAPI.deleteSlot(slot.id);
      alert('Slot deleted successfully');
      loadSlots();
    } catch (error) {
      console.error('Failed to delete slot:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete slot';
      alert(errorMessage);
    }
  };

  /**
   * Handle delete slot and booking (cross button)
   */
  const handleDeleteSlotAndBooking = async (slot) => {
    setDeleteDialogSlot(slot);
    setDeleteDialogAction('deleteSlotAndBooking');
    await loadPaymentInfo(slot.id);
    setShowDeleteDialog(true);
  };

  /**
   * Handle cancel booking only (delete icon for booked slots)
   */
  const handleCancelBooking = async (slot) => {
    setDeleteDialogSlot(slot);
    setDeleteDialogAction('cancelBooking');
    await loadPaymentInfo(slot.id);
    setShowDeleteDialog(true);
  };

  /**
   * Confirm delete slot and booking
   */
  const confirmDeleteSlotAndBooking = async () => {
    if (!deleteDialogSlot) return;

    try {
      const response = await availabilityAPI.deleteSlot(deleteDialogSlot.id);
      const successMessage = response.data.appointment_deleted
        ? 'Booked slot and associated appointment deleted successfully'
        : 'Slot deleted successfully';
      alert(successMessage);
      setShowDeleteDialog(false);
      setDeleteDialogSlot(null);
      setPaymentInfo(null);
      loadSlots();
    } catch (error) {
      console.error('Failed to delete slot:', error);
      const errorMessage = error.response?.data?.error || 'Failed to delete slot';
      alert(errorMessage);
    }
  };

  /**
   * Confirm cancel booking only
   */
  const confirmCancelBooking = async () => {
    if (!deleteDialogSlot) return;

    try {
      await availabilityAPI.cancelBooking(deleteDialogSlot.id);
      alert('Booking cancelled successfully. Availability slot has been retained.');
      setShowDeleteDialog(false);
      setDeleteDialogSlot(null);
      setPaymentInfo(null);
      loadSlots();
    } catch (error) {
      console.error('Failed to cancel booking:', error);
      const errorMessage = error.response?.data?.error || 'Failed to cancel booking';
      alert(errorMessage);
    }
  };

  /**
   * Publish all unpublished slots
   */
  const handlePublish = async () => {
    const unpublishedCount = slots.filter(s => !s.is_published).length;

    if (unpublishedCount === 0) {
      alert('No unpublished slots to publish');
      return;
    }

    if (!window.confirm(`Publish ${unpublishedCount} slot(s) to client view?`)) {
      return;
    }

    try {
      setPublishLoading(true);
      const response = await availabilityAPI.publishSlots(partnerId);
      alert(`Successfully published ${response.data.count} slot(s)`);
      loadSlots();
    } catch (error) {
      console.error('Failed to publish slots:', error);
      alert('Failed to publish slots. Please try again.');
    } finally {
      setPublishLoading(false);
    }
  };

  const bookedStatuses = ['booked', 'confirmed', 'confirmed_balance_pending', 'confirmed_payment_pending'];
  const unpublishedCount = slots.filter(s => !s.is_published && !bookedStatuses.includes(s.status)).length;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-2">Manage Availability</h2>
        <p className="text-gray-600 dark:text-dark-text-secondary">
          Create availability slots for the upcoming 4 weeks. Clients can view and book published slots.
        </p>
      </div>

      {/* Two Column Layout for Forms */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Booking Fee Card (left) */}
        <BookingFeeCard partnerId={partnerId} onFeeUpdate={loadFeeSettings} />

        {/* Slot Creation Form (right) */}
        <AvailabilitySlotForm
          formData={formData}
          onChange={setFormData}
          onSubmit={handleCreateSlot}
          loading={loading}
          partnerId={partnerId}
          therapyFees={feeSettings?.therapy_fees || []}
          googleCalendarConnected={googleCalendarConnected}
        />
      </div>

      {/* Publish Button */}
      <div className="flex items-center justify-between bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md p-4">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">Publish to Client View</h3>
          <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
            {unpublishedCount > 0
              ? `You have ${unpublishedCount} unpublished slot(s)`
              : 'All slots are published'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadSlots}
            disabled={loading}
            className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md text-gray-700 dark:text-dark-text-secondary bg-white dark:bg-dark-bg-secondary hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handlePublish}
            disabled={unpublishedCount === 0 || publishLoading}
            className={`flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${
              unpublishedCount === 0 || publishLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <Upload className="h-4 w-4" />
            {publishLoading ? 'Publishing...' : 'Update Client View'}
          </button>
        </div>
      </div>

      {/* Calendar View */}
      {loading && slots.length === 0 ? (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md p-8 text-center">
          <RefreshCw className="h-8 w-8 text-gray-400 dark:text-dark-text-tertiary mx-auto mb-3 animate-spin" />
          <p className="text-gray-500 dark:text-dark-text-tertiary">Loading availability slots...</p>
        </div>
      ) : (
        <AvailabilityCalendar
          slots={slots}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onCancelBooking={handleCancelBooking}
          onDeleteSlotAndBooking={handleDeleteSlotAndBooking}
          viewMode="partner"
        />
      )}

      {/* Conflict Warning Modal */}
      {showConflictModal && (
        <ConflictWarningModal
          conflict={conflictWarning}
          onProceed={proceedWithCreation}
          onCancel={cancelConflict}
        />
      )}

      {/* Delete Warning Dialog */}
      {showDeleteDialog && deleteDialogSlot && (
        <DeleteWarningDialog
          isOpen={showDeleteDialog}
          onClose={() => {
            setShowDeleteDialog(false);
            setDeleteDialogSlot(null);
            setPaymentInfo(null);
          }}
          onConfirm={deleteDialogAction === 'deleteSlotAndBooking' ? confirmDeleteSlotAndBooking : confirmCancelBooking}
          slot={deleteDialogSlot}
          paymentInfo={paymentInfo}
          actionType={deleteDialogAction}
        />
      )}

      {/* Max Appointments Warning Dialog */}
      {showMaxAppointmentsWarning && maxAppointmentsWarningValue !== null && (
        <MaxAppointmentsWarningDialog
          isOpen={showMaxAppointmentsWarning}
          maxAppointments={maxAppointmentsWarningValue}
          dontShowAgain={dontShowMaxAppointmentsWarningAgain}
          onToggleDontShowAgain={setDontShowMaxAppointmentsWarningAgain}
          onClose={async () => {
            if (dontShowMaxAppointmentsWarningAgain) {
              const key = getMaxAppointmentsWarningDismissKey(maxAppointmentsWarningValue);
              if (key) localStorage.setItem(key, 'true');
            }
            setShowMaxAppointmentsWarning(false);
            setMaxAppointmentsWarningValue(null);
            setDontShowMaxAppointmentsWarningAgain(false);
            
            // If there's pending slot data, proceed with creation after warning is acknowledged
            if (pendingSlotData) {
              await proceedWithSlotCreationAfterWarning();
            }
          }}
        />
      )}
    </div>
  );
};

export default AvailabilityTab;
